import { api } from './client';

/**
 * Turning a GPS fix into an address someone can read.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS GOES THROUGH OUR OWN BACKEND
 * ---------------------------------------------------------------------------
 * `LocationScreen` gets real coordinates from the device, and until now that
 * is all it could show - "22.71960, 75.85770" where a street name belongs.
 * Naming that point needs a geocoding service, and calling one directly from
 * the app would mean shipping its credential inside the APK, where it can be
 * extracted. It would also break OpenStreetMap's one-request-per-second rule
 * the moment two people opened the app at once.
 *
 * So the lookup is `GET /location/reverse` on our own server, which holds the
 * credential and the rate limit in one place. See `location.service.ts` in the
 * backend.
 *
 * The endpoint is public - this runs during onboarding, before there is an
 * account to authenticate with.
 */

export type ResolvedPlace = {
  /** One line, ready to display. Never empty. */
  label: string;
  /** House number and street, where the data has them. */
  line1?: string;
  area?: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
  /**
   * Every city name the geocoder offered, widest last.
   *
   * Kept alongside `city` rather than replacing it: `city` is what gets shown,
   * this is what gets MATCHED. Google answers with a widening chain - "Indore",
   * "Indore Division" - and serviceability has to consider all of them, while
   * the panel only ever has room for the first.
   */
  cityCandidates?: string[];
  /** Echoed back, so a label can be paired with the pin it came from. */
  lat: number;
  lng: number;
};

/**
 * The backend's actual `/geo/reverse-geocode` shape.
 *
 * Not the same fields as `ResolvedPlace` - no `label`/`line1`/`lat`/`lng`,
 * and `area` is an object here, not a string. `reverseGeocode` below is the
 * one place that translates between the two, so the rest of the app never
 * has to know the wire shape.
 */
type ReverseGeocodeResponse = {
  addressLine: string;
  cityCandidates?: string[];
  localityCandidates?: string[];
  stateName?: string;
  postalCode?: string;
  area?: { areaId: string; areaName: string; cityId: string; cityName: string };
};

/**
 * The house-and-street part of a full address line.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 * ---------------------------------------------------------------------------
 * `line1` used to be left unset, and that is what made a saved address show as
 * just an area name. `placeFromPin` picks its title as
 * `line1 || area || city || label`, so with no `line1` every pin fell through
 * to the AREA - "Vijay Nagar" - and the flat number and street the customer
 * had just watched the app resolve were nowhere, even though the server had
 * sent the whole thing in `addressLine` all along.
 *
 * The server sends one flat string: "12, MG Road, Vijay Nagar, Indore, Madhya
 * Pradesh 452010". The parts after the street are exactly the fields it also
 * sends separately, so the street is what is LEFT once those are removed from
 * the tail.
 *
 * Trimmed off the end only, never the middle: "Indore" appears in "Indore
 * Public School, Vijay Nagar, Indore" twice, and a blanket replace would
 * mangle the building's own name into ", Vijay Nagar,". Walking segments from
 * the back and stopping at the first one that is NOT a known part keeps the
 * name intact.
 *
 * A segment must EQUAL a known part, or be that part plus a postcode - not
 * merely contain it. "Indore Public School" contains "Indore" and is the one
 * segment that must survive; matching on `includes` deleted the whole address
 * and left it showing as the area again, which is the very bug this fixes.
 *
 * Returns undefined rather than an empty string when nothing is left - an
 * address that is only ever "Indore, Madhya Pradesh" has no street to show,
 * and `placeFromPin` should fall through to the area as it always did.
 */
/**
 * Google's Plus Code, taken off the front of an address line.
 *
 * The geocoder answers a pin with no street number as "PWF3+GJ6 Paramount
 * Apartments, 109, Telephone Nagar, Indore" - that leading token is an
 * Open Location Code, a grid reference for the square the pin sits in. It is
 * a real, resolvable address, which is why the upstream sends it, but it is
 * addressed to a machine: nobody types it, reads it out to a professional, or
 * recognises their own home in it.
 *
 * The shape is fixed by the spec: four or more characters from a 20-letter
 * alphabet, a "+", then two or three more. It only ever leads the line, so
 * anchoring to the start means a building genuinely containing a "+" further
 * along keeps it.
 *
 * If the code is the ONLY thing there, the line is left exactly as it was.
 * A bare Plus Code is a poor label but it is the sole identifier that point
 * has, and returning an empty string would strand the caller with nothing.
 */
const withoutPlusCode = (line: string): string => {
  const stripped = line.replace(/^[23456789CFGHJMPQRVWX]{4,}\+[23456789CFGHJMPQRVWX]{2,3}\s*,?\s*/i, '');
  return stripped.trim() || line;
};

/*
 * The one part of a Google address line the server never sends back as a
 * field of its own - and the reason every saved address used to read
 * "…, Indore, Madhya Pradesh 452018, India, Telephone Nagar, Indore, 452018".
 *
 * The peel below stops at the first segment it does not recognise, working
 * from the end. With the country unrecognised it stopped immediately, so the
 * "street" came out as the WHOLE line, and `placeFromPin` then wrote the area,
 * city and postcode underneath it a second time.
 *
 * Only India, because that is the only country the platform operates in and a
 * longer list would start matching place names - "Jordan" and "Chad" are both
 * localities somewhere.
 */
const COUNTRY_SEGMENTS = ['india'];

const streetOf = (
  addressLine: string,
  ...parts: Array<string | undefined>
): string | undefined => {
  const known = [
    ...parts
      .filter((part): part is string => !!part)
      .map(part => part.toLowerCase()),
    ...COUNTRY_SEGMENTS,
  ];

  const segments = addressLine.split(',').map(segment => segment.trim());

  /*
   * Equal, or the part with a postcode stuck on the end - the geocoder returns
   * the state and postcode as one segment, "Madhya Pradesh 452010".
   *
   * Deliberately NOT `includes`: that also matches "Indore Public School"
   * against "Indore" and eats a building name that is the whole point of the
   * line. Stripping the digits and re-comparing is the narrow version.
   */
  const isKnown = (segment: string) => {
    const lower = segment.toLowerCase();
    if (known.some(part => lower === part)) {
      return true;
    }
    /* "madhya pradesh 452010" -> "madhya pradesh"; a bare "452010" -> "". */
    const withoutPostcode = lower.replace(/\s*\b\d{4,}\b\s*/g, ' ').trim();
    return (
      withoutPostcode === '' || known.some(part => withoutPostcode === part)
    );
  };

  let end = segments.length;
  while (end > 0 && isKnown(segments[end - 1])) {
    end -= 1;
  }

  const street = segments.slice(0, end).join(', ').trim();
  return street || undefined;
};

/**
 * Resolve a pin to an address.
 *
 * THROWS on failure - `ApiError`, like every other service. The caller is
 * expected to catch it and keep showing the coordinates: a point in the sea
 * (404) and an unreachable upstream (502) are both perfectly ordinary here,
 * and neither is a reason to stop the flow. The address is a nicety; the
 * coordinates are what the booking actually travels on.
 *
 * `signal` matters more than usual. This is called on every pin drag, and a
 * screen that unmounts mid-drag would otherwise leave a request in flight
 * whose result lands on a dead component.
 */
export const reverseGeocode = async (
  lat: number,
  lng: number,
  signal?: AbortSignal,
): Promise<ResolvedPlace> => {
  const raw = await api.get<ReverseGeocodeResponse>('/geo/reverse-geocode', {
    query: { lat, lng },
    // No bearer token: the endpoint is public, and presenting an expired one
    // during onboarding would trip the 401 handler before anyone has signed in.
    anonymous: true,
    signal,
  });

  const area = raw.localityCandidates?.[0] ?? raw.area?.areaName;
  const city = raw.cityCandidates?.[0] ?? raw.area?.cityName;

  /* Stripped once, here, so nothing downstream ever sees a Plus Code: `label`
     and `line1` are both derived from this line, and the screen shows both. */
  const addressLine = withoutPlusCode(raw.addressLine);

  return {
    label: addressLine,
    line1: streetOf(addressLine, area, city, raw.stateName, raw.postalCode),
    area,
    city,
    cityCandidates: raw.cityCandidates ?? (
      raw.area?.cityName ? [raw.area.cityName] : undefined
    ),
    state: raw.stateName,
    postcode: raw.postalCode,
    lat,
    lng,
  };
};

/**
 * What a search result is: an address AND the pin it stands for.
 *
 * The coordinates are the whole reason this goes through a geocoder rather
 * than a text filter. `customer_addresses` stores a lat/lng and resolves a
 * city from it, so a result without one is a line of text that cannot be
 * booked against - which is exactly what the seeded list used to hand back.
 */
export type PlaceResult = {
  /** Stable enough to use as a list key within one response. */
  id: string;
  /** First line - the bit a person recognises. Never empty. */
  title: string;
  /** Area, city, postcode. May be empty when the geocoder knows no more. */
  subtitle: string;
  lat: number;
  lng: number;
};

/**
 * Look up addresses by name.
 *
 * The forward half of `reverseGeocode`, and public for the same reason: this
 * runs during onboarding, before there is an account to authenticate with.
 * The credential and the upstream rate limit stay on our server - see
 * `GeoController.searchPlaces` and the `GeocoderPort` adapters in the backend.
 *
 * THROWS on failure, like every other service. A search that cannot reach the
 * server is a real dead end for the caller - unlike a failed reverse geocode,
 * there are no coordinates to fall back on - so the screen reports it and
 * offers Retry rather than showing an empty list, which would read as "no such
 * place" when the truth is "we never asked".
 *
 * `signal` matters: this fires as the customer types, so a keystroke has to be
 * able to abandon the request the previous one started.
 */
export const searchPlaces = (
  query: string,
  signal?: AbortSignal,
): Promise<PlaceResult[]> =>
  api.get<PlaceResult[]>('/geo/search', {
    query: { q: query },
    anonymous: true,
    signal,
  });

/**
 * A city the platform actually operates in.
 *
 * From `GET /cities`, which is the authoritative list - at the time of writing
 * it is Indore and Bhopal. Everything else is somewhere we cannot send anyone.
 */
export type ServiceCity = {
  id: string;
  name: string;
  state?: string;
  isActive: boolean;
};

/**
 * Where the platform operates.
 *
 * Public, and deliberately so: the whole point is to answer "do you cover me?"
 * during onboarding, before there is an account.
 */
export const fetchCities = (signal?: AbortSignal): Promise<ServiceCity[]> =>
  api.get<ServiceCity[]>('/cities', { anonymous: true, signal });

/**
 * Whether a resolved pin is somewhere we can actually send a professional.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS MATCHES ON CITY NAME AND NOT ON `area`
 * ---------------------------------------------------------------------------
 * `/geo/reverse-geocode` returns an `area` object that looks like the obvious
 * signal, and it is not usable: it comes back `null` for a pin in the middle
 * of Indore, which IS a serviceable city. Trusting it would tell a customer
 * standing in the one city we cover best that we do not cover them.
 *
 * So the comparison is the geocoder's `cityCandidates` against `/cities`.
 * Google returns several candidates of widening scope - "Indore", then "Indore
 * Division" - and any of them matching is enough, which is what makes a pin on
 * the edge of the metro resolve the same way as one downtown.
 *
 * Compared case- and whitespace-insensitively because the two sides come from
 * different systems: one is Google's spelling, the other is whatever was typed
 * into the platform's own admin.
 */
export const isServiceable = (
  candidates: string[] | undefined,
  cities: ServiceCity[],
): boolean => matchServiceCity(candidates, cities) !== null;

/**
 * The same match, but keeping WHICH city it was.
 *
 * `isServiceable` throws that away, and the id is worth having: the home
 * screen's published configuration is targeted per city, so asking for it
 * without one only ever resolves the global publication and a city's own
 * banners are invisible to the very customers standing in it. This is the
 * only place in the app that can answer "which of our cities is this pin in"
 * without a second round trip - the work is already done here.
 *
 * Candidates are ordered widening ("Indore", then "Indore Division"), so the
 * FIRST one that matches is the narrowest, which is the one to keep.
 */
export const matchServiceCity = (
  candidates: string[] | undefined,
  cities: ServiceCity[],
): ServiceCity | null => {
  if (!candidates?.length) {
    return null;
  }

  const normalise = (value: string) => value.trim().toLowerCase();
  const active = cities.filter(city => city.isActive);

  for (const candidate of candidates) {
    const found = active.find(
      city => normalise(city.name) === normalise(candidate),
    );
    if (found) {
      return found;
    }
  }
  return null;
};

/**
 * The customer-facing way of saying "we do not cover this address yet".
 *
 * One string, exported, because it is said in more than one place - the sheet
 * that stops a checkout and the alert when a save is refused - and two
 * hand-written versions of the same news drift into two different promises
 * about when we are arriving.
 *
 * Deliberately only the half that is true EVERYWHERE. What happens next is
 * not: a refused save can still offer to keep the address on file, while a
 * blocked checkout cannot promise anything of the sort. Each caller adds its
 * own next step, so neither has to say something the other would contradict.
 */
export const NOT_SERVICEABLE_MESSAGE =
  "We're not in your area yet. We're expanding fast and hope to reach you soon.";

/**
 * Whether a failed request failed BECAUSE the pin is outside our cities.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS MATCHES ON TEXT
 * ---------------------------------------------------------------------------
 * The server rejects an unserviceable address with a 400 and the sentence
 * "This pin is outside the cities currently served by the platform" - a plain
 * validation failure carrying no code of its own, and 400 is also what a
 * malformed line or a missing pin returns. So the status cannot tell the two
 * apart and the wording is the only signal there is.
 *
 * That is a fragile hinge and it is deliberately arranged to fail SAFE: a
 * caller that does not recognise this falls back to showing the server's own
 * message, which is accurate if blunt. The cost of a missed match is a less
 * friendly sentence, not a wrong one - so a server rewording this breaks the
 * tone and never the truth.
 *
 * Matched on the distinctive middle of the sentence rather than the whole of
 * it, so punctuation or a trailing city list does not defeat it.
 */
export const isNotServiceableError = (failure: unknown): boolean => {
  const message =
    failure instanceof Error ? failure.message : String(failure ?? '');
  const lower = message.toLowerCase();

  return (
    lower.includes('outside the cities') ||
    lower.includes('not serviceable') ||
    lower.includes('no serviceable city')
  );
};
